<template>
  <div class="title">
    Upcoming Events
  </div>
  <div @click="maximize" class="menu-button">
    <i class="pi pi-window-maximize"></i>
  </div>
  <div
    @mouseenter="showScrollbar"
    @mouseleave="hideScrollbar"
    id="events-list-container"
    class="container no-scrollbar custom-scrollbar"
  >
    <div v-if="weekEvents && weekEvents.length">
      <div v-for="(calEvent, i) in weekEvents" :key="i" class="event-card">
        <i
          v-if="displayChevron(calEvent)"
          @click="toggleExpandFields(calEvent)"
          class="pi chevron-btn"
          :class="
            expand === calEvent.id
              ? 'pi-chevron-circle-up'
              : 'pi-chevron-circle-down'
          "
        ></i>
        <div class="card-header">
          <div v-tooltip="tooltipDate(calEvent)" class="date">
            <div>{{ calEvent.startDay }}</div>
            <div>{{ calEvent.startMonth }}</div>
          </div>
          <div class="card-header-details">
            <div :id="calEvent.id" class="name">{{ calEvent.title }}</div>
            <div class="times" v-if="calEvent.time || calEvent.rangeDays">
              <div v-if="calEvent.time">
                <span class="field-name">Start </span>
                {{ calEvent.startTime }}
              </div>
              <div>
                <span class="field-name">End </span>
                <span v-if="calEvent.rangeDays">
                  {{ formatDate(calEvent.end, "DD MMM") }}
                  {{ calEvent.endTime ? "- " : "" }}
                </span>
                {{ calEvent.endTime }}
              </div>
            </div>
          </div>
        </div>
        <div class="expand-container">
          <transition name="expand">
            <div v-if="expand === calEvent.id" class="expand-fields">
              <div v-if="calEvent.showTitleField" class="field">
                <span class="field-name">Name: </span>{{ calEvent.title }}
              </div>
              <div v-if="calEvent.address" class="field">
                <span class="field-name">Address: </span>{{ calEvent.address }}
              </div>
              <div v-if="calEvent.notes" class="field">
                <span class="field-name">Notes: </span>{{ calEvent.notes }}
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
    <div v-else class="p-text-center p-text-italic" style="color: #fff;">
      Events for the next week appear here. Click in the top right corner to
      open the calendar and add events or see all events.
    </div>
  </div>
</template>

<style lang="scss" scoped>
.container {
  overflow-y: overlay;
}
.event-card {
  padding: 0.5rem;
  margin: 0 0.7rem 0.5rem 0;
  border-radius: 6px;
  background: #1f5d7e;
  box-shadow: $small-shadow;
  position: relative;
  &:last-child {
    margin-bottom: 0;
  }
  &:hover {
    .chevron-btn {
      opacity: 1;
    }
  }
}

.chevron-btn {
  font-size: 1.3rem;
  cursor: pointer;
  width: 30px;
  position: absolute;
  top: 1rem;
  right: 0;
  color: #fff;
  opacity: 0;
  transition: opacity $animation-fast ease-in;
}

.times {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  font-size: 0.9rem;
  margin: 0.5rem 0 0 0;
  div:first-child {
    margin-right: 0.5rem;
  }
}

.date {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0.2rem;
  font-size: 1.2rem;
  font-weight: 600;
  border-radius: 6px;
  cursor: default;
  color: var(--surface-50);
  background: #a6d1b5;
}

.expand-fields {
  margin-top: 1rem;
  color: #fff;

  .field {
    &:not(:first-child) {
      margin-top: 0.5rem;
    }
    word-break: break-all;
    background: #21688e;
    border-radius: 6px;
    padding: 0.5rem;
  }
}

.field-name {
  font-weight: 600 !important;
}

.card-header {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  color: #fff;
}

.card-header-details {
  margin-left: 0.5rem;
  overflow-x: auto;
  flex: 1;
  padding-right: 0.5rem;
}

.name {
  font-size: 1.1rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  overflow-wrap: break-word;
  max-width: calc(100% - 1rem);
}

/* ANIMATIONS */
.expand-enter-active {
  transition: max-height 1s ease, opacity 0.2s ease;
}

.expand-leave-active {
  transition: max-height 0.2s ease, opacity 0.2s ease;
}

.expand-enter-to,
.expand-leave-from {
  opacity: 1;
  max-height: 500px;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  max-height: 0;
}
</style>

<script>
import { ref, toRefs, watch } from "vue";
import useEvents from "../composables/events.js";
import moment from "moment";
import Tooltip from "primevue/tooltip";
export default {
  props: {
    projectId: {
      required: true,
      type: String
    },
    calendarOpen: {
      required: true,
      type: Boolean
    }
  },
  directives: {
    tooltip: Tooltip
  },
  emits: ["expand"],
  setup(props, context) {
    const propsRefs = toRefs(props);
    const calOpen = propsRefs.calendarOpen;

    const { fetchEvents: use_fetchEvents } = useEvents();
    const datesRange = { start: null, end: null };
    const weekEvents = ref(null);
    const expand = ref(null);

    getCurrentWeekEvents();

    watch(calOpen, val => {
      if (val === false) getCurrentWeekEvents();
    });

    async function getCurrentWeekEvents() {
      datesRange.start = moment()
        .startOf("isoweek")
        .toDate();
      datesRange.end = moment()
        .endOf("isoweek")
        .toDate();
      const events = await use_fetchEvents(props.projectId, datesRange, true);
      events.forEach(event => {
        event.start = convertToDate(event.start);
        event.startDay = moment(event.start).format("DD");
        event.startMonth = moment(event.start).format("MMMM");
        event.expand = false;

        if (event.end) {
          event.end = convertToDate(event.end);
        }
        if (event.time) {
          event.startTime = convertToTime(event.start);
          event.endTime = convertToTime(event.end);
        }
      });
      weekEvents.value = events;
    }

    function convertToDate(timestamp) {
      return moment(timestamp.seconds * 1000).toDate();
    }

    function formatDate(date, format) {
      return moment(date).format(format);
    }

    function convertToTime(date) {
      return moment(date).format("HH:mm");
    }

    function tooltipDate(event) {
      let fullDate;
      if (!event.rangeDays) {
        if (!event.time) {
          fullDate = moment(event.start).format("dddd DD MMMM YYYY");
        } else {
          fullDate = `${moment(event.start).format("dddd DD MMMM YYYY")}, ${
            event.startTime
          } - ${event.endTime} `;
        }
      } else {
        if (!event.time) {
          fullDate = `${moment(event.start).format(
            "dddd DD MMMM YYYY"
          )} - ${moment(event.end).format("dddd DD MMMM YYYY")}`;
        } else {
          fullDate = `${moment(event.start).format("dddd DD MMMM YYYY")}, ${
            event.startTime
          }  - ${moment(event.end).format("dddd DD MMMM YYYY")}, ${
            event.endTime
          } `;
        }
      }

      return fullDate;
    }

    function checkEllipsis(id) {
      const title = document.getElementById(id);
      return title.offsetWidth < title.scrollWidth;
    }

    function toggleExpandFields(event) {
      if (checkEllipsis(event.id)) {
        event.showTitleField = true;
      }
      expand.value === event.id
        ? (expand.value = null)
        : (expand.value = event.id);
    }

    function maximize() {
      context.emit("expand");
    }

    function showScrollbar() {
      const eventsListWidget = document.getElementById("events-list-container");
      eventsListWidget.classList.remove("no-scrollbar");
    }

    function hideScrollbar() {
      const eventsListWidget = document.getElementById("events-list-container");
      eventsListWidget.classList.add("no-scrollbar");
    }

    function displayChevron(calEvent) {
      return calEvent.showTitleField || calEvent.address || calEvent.notes;
    }

    return {
      weekEvents,
      displayChevron,
      toggleExpandFields,
      expand,
      tooltipDate,
      formatDate,
      maximize,
      showScrollbar,
      hideScrollbar
    };
  }
};
</script>
